import { useCallback, useRef } from "react";
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import { useAcknowledgeAdditionalStainNoReportChange } from "../../hooks/useWorkflows";
import { getApiErrorStatus } from "../../utils/apiError";
import { createUuid } from "../../utils/createUuid";

export interface AdditionalStainNoReportChangeProjection {
  eligible: boolean;
  eventId: string | null;
  expectedAssignmentRevision: number;
  restoreStatusName: "Signed Off" | null;
}

interface AdditionalStainNoReportChangeActionProps {
  caseId: string;
  projection?: AdditionalStainNoReportChangeProjection | null;
}

export function AdditionalStainNoReportChangeAction({
  caseId,
  projection,
}: AdditionalStainNoReportChangeActionProps) {
  const navigate = useNavigate();
  const acknowledgement = useAcknowledgeAdditionalStainNoReportChange();
  const [opened, { open, close }] = useDisclosure(false);
  const requestId = useRef<string>();

  const handleOpen = useCallback(() => {
    requestId.current = createUuid();
    open();
  }, [open]);

  const handleClose = useCallback(() => {
    if (acknowledgement.isPending) return;
    close();
  }, [acknowledgement.isPending, close]);

  const handleConfirm = useCallback(() => {
    if (!projection?.eventId || !requestId.current) return;
    acknowledgement.mutate(
      {
        caseId,
        eventId: projection.eventId,
        expectedAssignmentRevision: projection.expectedAssignmentRevision,
        requestId: requestId.current,
      },
      {
        onSuccess: () => {
          close();
          notifications.show({
            color: "green",
            title: "Additional stains reviewed",
            message:
              "The case was returned to Signed Off with no report change.",
          });
          navigate("/pathologist-review");
        },
        onError: (error) => {
          const status = getApiErrorStatus(error);
          notifications.show({
            color: "red",
            title: "No report change was not saved",
            message:
              status === 409
                ? "The case changed or has other review work. Refresh it before trying again."
                : status === 403
                ? "You do not have permission to resolve this review."
                : "The review could not be resolved. Please try again.",
          });
        },
      }
    );
  }, [acknowledgement, caseId, close, navigate, projection]);

  if (projection?.eligible !== true) return null;

  return (
    <>
      <Button
        variant="light"
        color="teal"
        leftSection={<IconCheck size={16} />}
        onClick={handleOpen}
      >
        No report change
      </Button>
      <Modal
        opened={opened}
        onClose={handleClose}
        title="Confirm no report change"
        centered
        size="md"
        closeOnClickOutside={!acknowledgement.isPending}
        closeOnEscape={!acknowledgement.isPending}
      >
        <Stack gap="md">
          <Text size="sm">
            Confirm that the returned additional stains require no report
            change. This will acknowledge the stains and return the case to
            Signed Off.
          </Text>
          <Alert
            color="blue"
            variant="light"
            icon={<IconAlertCircle size={16} />}
          >
            The signed report and its contents will not be changed.
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={handleClose}
              disabled={acknowledgement.isPending}
            >
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={handleConfirm}
              loading={acknowledgement.isPending}
            >
              Confirm no change
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
